import { Component } from 'react';
import { withTranslation } from 'react-i18next';
import { requestIdOf } from '../states/status.js';
import './ErrorBoundary.css';

/**
 * Catches render-time crashes and replaces *only the subtree it wraps* with a
 * recoverable panel (spec §11 / §11.1).
 *
 * Three things this deliberately does:
 *   - It keeps the surrounding context. The fallback is a card inside the
 *     layout, not a full-page takeover, so the shell, navigation and anything
 *     the user still has open stay usable.
 *   - It offers both escapes: "retry" re-mounts the subtree (cheap, keeps app
 *     state and any persisted draft), "reload" is the last resort.
 *   - It never shows a stack trace in production. Users get plain language;
 *     the request id — the one technical token support actually needs — is
 *     shown in every environment when the error carries one.
 *
 * @param {object} props
 * @param {import('react').ReactNode} props.children
 * @param {boolean} [props.showDetails] - opt out of the technical detail block
 *   with `false`. Ignored outside DEV builds, which never show it.
 * @param {import('react').ReactNode} [props.title] - overrides the headline.
 * @param {import('react').ReactNode} [props.description] - overrides the explanation.
 * @param {() => void} [props.onReset] - extra cleanup to run when the user retries.
 * @param {(error: Error, info: object) => void} [props.onError] - reporting hook.
 */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleReset = () => {
    this.props.onReset?.();
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    const { t, title, description, showDetails, children } = this.props;
    const { hasError, error, errorInfo } = this.state;

    if (!hasError) return children;

    const isDev = Boolean(import.meta.env?.DEV);
    const detailsVisible = isDev && showDetails !== false;
    const requestId = requestIdOf(error);

    return (
      <div className="error-boundary" role="alert">
        <div className="error-boundary-content">
          <h2>{title || t('error.title')}</h2>
          <p className="error-message">{description || t('error.boundaryHint')}</p>

          {requestId && <p className="error-request-id">{t('error.requestId', { id: requestId })}</p>}

          {detailsVisible && (error || errorInfo) && (
            <details className="error-details">
              <summary>{t('error.details')}</summary>
              <pre>
                {error?.message || t('error.default')}
                {errorInfo?.componentStack || ''}
              </pre>
            </details>
          )}

          <div className="error-actions">
            <button type="button" onClick={this.handleReset} className="btn-retry">
              {t('error.tryAgain')}
            </button>
            <button type="button" onClick={() => window.location.reload()} className="btn-reload">
              {t('error.reloadPage')}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default withTranslation()(ErrorBoundary);
